import React, { useCallback, useEffect, useState } from 'react';
import { Plus, X } from 'lucide-react';
import {
  TaskyAlert,
  TaskyBadge,
  TaskyButton,
  TaskyInput,
  TaskyLabel,
  TaskySpinner,
  TaskyTable,
  TaskyTableBody,
  TaskyTableCell,
  TaskyTableHead,
  TaskyTableHeaderCell,
  TaskyTableRow,
  TaskyTextarea,
  useConfirm,
} from '../tasky';
import adminExternalApiTokenService, {
  ExternalApiToken,
  ExternalApiTokenAbility,
  ExternalApiTokenCreateResponse,
} from '../../services/api/adminExternalApiTokenService';

const ABILITY_OPTIONS: { value: ExternalApiTokenAbility; label: string; description: string }[] = [
  {
    value: 'image_styles:write',
    label: '画像スタイル登録',
    description: 'ads/banner などの画像スタイル登録と画像アップロードを許可します',
  },
  {
    value: 'video_styles:write',
    label: '動画スタイル登録',
    description: '動画スタイル（参考動画・レシピ JSON）の登録と公開・非公開の切り替えを許可します',
  },
];

const initialAbilities: ExternalApiTokenAbility[] = ['image_styles:write'];

const ExternalApiTokenList: React.FC = () => {
  const confirm = useConfirm();
  const [tokens, setTokens] = useState<ExternalApiToken[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [name, setName] = useState('');
  const [abilities, setAbilities] = useState<ExternalApiTokenAbility[]>(initialAbilities);
  const [expiresAt, setExpiresAt] = useState('');
  const [createdToken, setCreatedToken] = useState<ExternalApiTokenCreateResponse | null>(null);

  const fetchTokens = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const data = await adminExternalApiTokenService.getAll();
      setTokens(data);
    } catch (err) {
      console.error('Failed to fetch external API tokens:', err);
      setError('外部連携 API トークン一覧の取得に失敗しました');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchTokens();
  }, [fetchTokens]);

  const openCreateModal = () => {
    setName('');
    setAbilities(initialAbilities);
    setExpiresAt('');
    setCreatedToken(null);
    setIsModalOpen(true);
  };

  const closeCreateModal = () => {
    setIsModalOpen(false);
    setCreatedToken(null);
  };

  const handleAbilityChange = (ability: ExternalApiTokenAbility, checked: boolean) => {
    setAbilities(prev => {
      if (checked) {
        return prev.includes(ability) ? prev : [...prev, ability];
      }
      return prev.filter(item => item !== ability);
    });
  };

  const handleCreate = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!name.trim()) {
      setError('トークン名を入力してください');
      return;
    }
    if (abilities.length === 0) {
      setError('権限を1つ以上選択してください');
      return;
    }

    setIsLoading(true);
    setError(null);
    try {
      const created = await adminExternalApiTokenService.create({
        name: name.trim(),
        abilities,
        expires_at: expiresAt || null,
      });
      setCreatedToken(created);
      await fetchTokens();
    } catch (err: any) {
      console.error('Failed to create external API token:', err);
      setError(err.response?.data?.message || '外部連携 API トークンの発行に失敗しました');
    } finally {
      setIsLoading(false);
    }
  };

  const handleRevoke = async (token: ExternalApiToken) => {
    if (!(await confirm({ title: `「${token.name}」を失効します。よろしいですか？`, tone: 'destructive', confirmText: '失効する' }))) return;

    setIsLoading(true);
    try {
      await adminExternalApiTokenService.revoke(token.id);
      await fetchTokens();
    } catch (err) {
      console.error('Failed to revoke external API token:', err);
      setError('外部連携 API トークンの失効に失敗しました');
    } finally {
      setIsLoading(false);
    }
  };

  const handleDelete = async (token: ExternalApiToken) => {
    if (!(await confirm({ title: `「${token.name}」を削除します。よろしいですか？`, tone: 'destructive', confirmText: '削除する' }))) return;

    setIsLoading(true);
    try {
      await adminExternalApiTokenService.delete(token.id);
      await fetchTokens();
    } catch (err) {
      console.error('Failed to delete external API token:', err);
      setError('外部連携 API トークンの削除に失敗しました');
    } finally {
      setIsLoading(false);
    }
  };

  const copyCreatedToken = async () => {
    if (!createdToken?.plain_token) return;
    await navigator.clipboard.writeText(createdToken.plain_token);
  };

  const formatDate = (value?: string | null) => {
    if (!value) return '-';
    return new Date(value).toLocaleString('ja-JP');
  };

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-base font-semibold text-dark">外部連携 API トークン</h2>
        <TaskyButton size="sm" onClick={openCreateModal} disabled={isLoading}>
          <Plus />
          新規トークン発行
        </TaskyButton>
      </div>

      {/* Error message */}
      {error && (
        <TaskyAlert tone="error" className="mb-4">
          <div className="flex items-center gap-2">
            <span>{error}</span>
            <button
              type="button"
              onClick={() => setError(null)}
              aria-label="閉じる"
              className="shrink-0 text-dark/50 hover:text-dark transition-colors"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </TaskyAlert>
      )}

      {/* Tokens table */}
      {isLoading && tokens.length === 0 ? (
        <div className="flex items-center justify-center gap-3 py-10 text-sm text-gray-500">
          <TaskySpinner size="md" />
          読み込み中...
        </div>
      ) : (
        <TaskyTable>
          <TaskyTableHead>
            <TaskyTableRow hoverable={false}>
              <TaskyTableHeaderCell>ID</TaskyTableHeaderCell>
              <TaskyTableHeaderCell>名前</TaskyTableHeaderCell>
              <TaskyTableHeaderCell>Prefix</TaskyTableHeaderCell>
              <TaskyTableHeaderCell>権限</TaskyTableHeaderCell>
              <TaskyTableHeaderCell>状態</TaskyTableHeaderCell>
              <TaskyTableHeaderCell>最終利用</TaskyTableHeaderCell>
              <TaskyTableHeaderCell>有効期限</TaskyTableHeaderCell>
              <TaskyTableHeaderCell>作成者</TaskyTableHeaderCell>
              <TaskyTableHeaderCell>操作</TaskyTableHeaderCell>
            </TaskyTableRow>
          </TaskyTableHead>
          <TaskyTableBody>
            {tokens.length === 0 ? (
              <TaskyTableRow hoverable={false}>
                <TaskyTableCell colSpan={9} className="py-8 text-center text-gray-500">
                  外部連携 API トークンはまだ発行されていません
                </TaskyTableCell>
              </TaskyTableRow>
            ) : (
              tokens.map(token => (
                <TaskyTableRow key={token.id}>
                  <TaskyTableCell className="text-gray-400">{token.id}</TaskyTableCell>
                  <TaskyTableCell className="font-medium">{token.name}</TaskyTableCell>
                  <TaskyTableCell>
                    <code className="font-mono text-xs bg-gray-100 text-gray-700 rounded px-1.5 py-0.5">
                      {token.token_prefix}...
                    </code>
                  </TaskyTableCell>
                  <TaskyTableCell className="text-xs text-gray-600">{token.abilities.join(', ')}</TaskyTableCell>
                  <TaskyTableCell>
                    <TaskyBadge variant={token.is_active ? 'success' : 'secondary'} dot>
                      {token.is_active ? '有効' : '無効'}
                    </TaskyBadge>
                  </TaskyTableCell>
                  <TaskyTableCell className="text-gray-500">{formatDate(token.last_used_at)}</TaskyTableCell>
                  <TaskyTableCell className="text-gray-500">{formatDate(token.expires_at)}</TaskyTableCell>
                  <TaskyTableCell className="text-gray-500">{token.created_by_admin?.email || '-'}</TaskyTableCell>
                  <TaskyTableCell>
                    <div className="flex items-center gap-3 whitespace-nowrap">
                      {token.is_active && (
                        <button
                          type="button"
                          onClick={() => handleRevoke(token)}
                          disabled={isLoading}
                          className="text-xs font-medium text-warning hover:underline disabled:opacity-50 disabled:no-underline"
                        >
                          失効
                        </button>
                      )}
                      <button
                        type="button"
                        onClick={() => handleDelete(token)}
                        disabled={isLoading}
                        className="text-xs font-medium text-destructive hover:underline disabled:opacity-50 disabled:no-underline"
                      >
                        削除
                      </button>
                    </div>
                  </TaskyTableCell>
                </TaskyTableRow>
              ))
            )}
          </TaskyTableBody>
        </TaskyTable>
      )}

      {/* Create modal */}
      {isModalOpen && (
        <div
          className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4"
          onClick={(event) => event.target === event.currentTarget && closeCreateModal()}
        >
          <div className="bg-white rounded-tasky-lg w-full max-w-[720px] max-h-[90vh] overflow-auto shadow-card">
            <div className="flex items-center justify-between px-6 py-4 border-b border-warm-border">
              <h5 className="text-lg font-semibold text-dark">外部連携 API トークン発行</h5>
              <button
                type="button"
                onClick={closeCreateModal}
                aria-label="閉じる"
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="px-6 py-5">
              {createdToken ? (
                <div>
                  <TaskyAlert tone="warning" className="mb-4">
                    このトークンは再表示できません。外部サービス側の Secret Manager や環境変数に保存してください。
                  </TaskyAlert>
                  <TaskyLabel>発行済みトークン</TaskyLabel>
                  <TaskyTextarea
                    readOnly
                    value={createdToken.plain_token}
                    rows={3}
                    className="font-mono text-sm"
                  />
                  <div className="flex justify-end gap-2 mt-4">
                    <TaskyButton type="button" variant="secondary" onClick={copyCreatedToken}>
                      コピー
                    </TaskyButton>
                    <TaskyButton type="button" onClick={closeCreateModal}>
                      閉じる
                    </TaskyButton>
                  </div>
                </div>
              ) : (
                <form onSubmit={handleCreate}>
                  <div className="mb-4">
                    <TaskyLabel htmlFor="external-token-name" required>
                      名前
                    </TaskyLabel>
                    <TaskyInput
                      id="external-token-name"
                      type="text"
                      value={name}
                      onChange={event => setName(event.target.value)}
                      placeholder="例: バナー生成外部連携"
                      maxLength={100}
                    />
                  </div>

                  <div className="mb-4">
                    <TaskyLabel required>権限</TaskyLabel>
                    {ABILITY_OPTIONS.map(option => (
                      <label key={option.value} className="block mb-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={abilities.includes(option.value)}
                          onChange={event => handleAbilityChange(option.value, event.target.checked)}
                          className="mr-2 h-4 w-4 rounded border-warm-border text-primary focus:ring-2 focus:ring-primary/40 focus:ring-offset-0"
                        />
                        <strong className="text-sm font-semibold text-dark">{option.label}</strong>
                        <span className="ml-2 text-xs text-gray-500">{option.value}</span>
                        <div className="ml-6 text-xs text-gray-500">{option.description}</div>
                      </label>
                    ))}
                  </div>

                  <div className="mb-4">
                    <TaskyLabel htmlFor="external-token-expiry">有効期限</TaskyLabel>
                    <TaskyInput
                      id="external-token-expiry"
                      type="datetime-local"
                      value={expiresAt}
                      onChange={event => setExpiresAt(event.target.value)}
                    />
                  </div>

                  <div className="flex justify-end gap-2 pt-4 border-t border-warm-border">
                    <TaskyButton type="button" variant="secondary" onClick={closeCreateModal} disabled={isLoading}>
                      キャンセル
                    </TaskyButton>
                    <TaskyButton type="submit" disabled={isLoading}>
                      発行
                    </TaskyButton>
                  </div>
                </form>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ExternalApiTokenList;
